import { DataTable } from "../components/DataTable/index.tsx";
import type { Col, DataTableRow } from "../components/DataTable/index.tsx";
import useTitleBar from "../hooks/useTitleBar.tsx";
import { helpers } from "../util/helpers.ts";
import { gradeColor, playoffResult } from "../util/reportCardHelpers.ts";
import type { View } from "../../common/types.ts";

const cols: Col[] = [
	{ title: "#", sortSequence: ["asc", "desc"], sortType: "number" },
	{ title: "Team" },
	{ title: "Grade", sortSequence: ["desc", "asc"], sortType: "number" },
	{ title: "GM Score", sortSequence: ["desc", "asc"], sortType: "number" },
	{ title: "Record" },
	{
		title: "Over/Under",
		desc: "Preseason projected wins",
		sortType: "number",
	},
	{ title: "Playoffs", sortType: "number" },
];

const ReportCardLeaderboard = ({
	numPlayoffRounds,
	season,
	teams,
	userTid,
}: View<"reportCardLeaderboard">) => {
	useTitleBar({
		title: "GM Score Leaderboard",
		jumpTo: true,
		jumpToSeason: season,
		dropdownView: "report_card_leaderboard",
		dropdownFields: { seasons: season },
	});

	if (teams.length === 0) {
		return (
			<div className="alert alert-info" style={{ maxWidth: 640 }}>
				<b>No report cards for {season} yet.</b> Report cards are generated at the
				end of a season, when the draft begins.
			</div>
		);
	}

	const rows: DataTableRow[] = teams.map((t) => ({
		key: t.tid,
		data: [
			t.rank,
			<a
				href={helpers.leagueUrl([
					"report_card",
					`${t.abbrev}_${t.tid}`,
					season,
				])}
			>
				{t.region} {t.name}
				{t.champion ? " 🏆" : ""}
			</a>,
			{
				// Sort the grade column by the underlying score.
				value: (
					<span style={{ color: gradeColor(t.grade), fontWeight: 600 }}>
						{t.grade}
					</span>
				),
				sortValue: t.gmScore,
			},
			t.gmScore,
			`${t.won}–${t.lost}`,
			{
				value: t.projectedWins ?? "—",
				sortValue: t.projectedWins ?? -1,
			},
			{
				value: playoffResult(t.playoffRoundsWon, numPlayoffRounds),
				sortValue: t.playoffRoundsWon,
			},
		],
		classNames: {
			"table-info": t.tid === userTid,
		},
	}));

	return (
		<>
			<p>
				Every team's season graded on one shareable <b>GM Score</b> — winning,
				beating expectations, player development, accolades, high draft picks,
				and cap management, with a championship worth the most. Click a team for
				its full report card.
			</p>

			<DataTable
				cols={cols}
				defaultSort={[0, "asc"]}
				name="ReportCardLeaderboard"
				pagination
				rows={rows}
			/>
		</>
	);
};

export default ReportCardLeaderboard;
